import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useFetch, useToast } from '../../../hooks'
import useAuthz from '../../../hooks/useAuthz'
import { isValidGoogleDocsUrl } from '../../../utils/urlValidation'
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material'
import { CloudUpload, Description, Delete, DeleteForever, Clear } from '@mui/icons-material'
import InfoIcon from '@mui/icons-material/Info'
import { InfoTooltip, ToastNotification } from '../../../components/atoms'
import styles from './SettingsSOP.module.scss'
import { getUserOrganization } from '../../../utils/sopUtils.js'
import { authenticatedFetch } from '../../../services/api/apiClient.js'
import { apiEndpoints } from '../../../consts'
import SOPFileCard from './components/SOPFileCard'
import GoogleDocsCard from './components/GoogleDocsCard'

const MAX_FILE_SIZE = 5 * 1024 * 1024
const ALLOWED_FILE_TYPES = ['image/svg+xml', 'image/svg']

const SettingsSOPPage = ({ user }) => {
  const { isAdmin } = useAuthz()
  const [, setSopUrl] = useState('')
  const [sopExists, setSopExists] = useState(false)
  const [uploadDate, setUploadDate] = useState(null)
  const [openDialog, setOpenDialog] = useState(false)
  const [loading, setLoading] = useState(true)
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [uploadConfirmDialog, setUploadConfirmDialog] = useState(false)
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState('')

  const [reuploadConfirmDialog, setReuploadConfirmDialog] = useState(false)

  const [googleDocsUrl, setGoogleDocsUrl] = useState('')
  const [googleDocsExists, setGoogleDocsExists] = useState(false)
  const [, setGoogleDocsLoading] = useState(true)
  const [googleDocsSaving, setGoogleDocsSaving] = useState(false)
  const [googleDocsDeleting, setGoogleDocsDeleting] = useState(false)
  const [googleDocsUrlInput, setGoogleDocsUrlInput] = useState('')

  const { toast, showToast, closeToast } = useToast()

  const { data: campaigns } = useFetch(apiEndpoints.campaigns, {
    initialData: [],
    skip: !isAdmin,
    transform: (data) => data || [],
    errorMessage: 'Error fetching campaigns'
  })

  const getCurrentCampaign = () => {
    if (isAdmin && selectedCampaign) {
      return selectedCampaign
    }
    return getUserOrganization(user)
  }

  useEffect(() => {
    if (!isAdmin || campaigns.length === 0 || selectedCampaign) return
    const userOrg = getUserOrganization(user)
    const userCampaign = campaigns.find((c) => c.original_name === userOrg)
    if (userCampaign) {
      setSelectedCampaign(userCampaign.original_name)
    } else {
      setSelectedCampaign(campaigns[0].original_name)
    }
  }, [isAdmin, campaigns, user, selectedCampaign])

  useEffect(() => {
    const fetchSopUrl = async () => {
      const campaign = getCurrentCampaign()
      if (!campaign) {
        setLoading(false)
        return
      }

      try {
        const response = await authenticatedFetch(apiEndpoints.campaignSop(campaign), {
          method: 'GET'
        })

        if (response.status === 404) {
          setSopExists(false)
          setSopUrl('')
          setUploadDate(null)
          setLoading(false)
          return
        }

        if (!response.ok) {
          setSopExists(false)
          setSopUrl('')
          setUploadDate(null)
          setLoading(false)
          return
        }

        const data = await response.json()
        if (data?.sop_url) {
          setSopUrl(data.sop_url)
          setSopExists(true)
          setUploadDate(new Date().toISOString())
        } else {
          setSopExists(false)
          setSopUrl('')
          setUploadDate(null)
        }
      } catch (error) {
        if (error.status !== 404) {
          console.error('Error fetching SOP:', error)
        }
        setSopExists(false)
        setSopUrl('')
        setUploadDate(null)
      } finally {
        setLoading(false)
      }
    }

    fetchSopUrl()
    // eslint-disable-next-line
  }, [user, selectedCampaign, isAdmin])

  useEffect(() => {
    const fetchGoogleDocsUrl = async () => {
      const campaign = getCurrentCampaign()
      if (!campaign) {
        setGoogleDocsLoading(false)
        return
      }

      try {
        const response = await authenticatedFetch(apiEndpoints.campaignGoogleDocs(campaign), {
          method: 'GET'
        })

        if (response.status === 404) {
          setGoogleDocsExists(false)
          setGoogleDocsUrl('')
          setGoogleDocsUrlInput('')
          setGoogleDocsLoading(false)
          return
        }

        if (!response.ok) {
          setGoogleDocsExists(false)
          setGoogleDocsUrl('')
          setGoogleDocsUrlInput('')
          setGoogleDocsLoading(false)
          return
        }

        const data = await response.json()
        if (data?.google_docs_url) {
          setGoogleDocsUrl(data.google_docs_url)
          setGoogleDocsUrlInput(data.google_docs_url)
          setGoogleDocsExists(true)
        } else {
          setGoogleDocsExists(false)
          setGoogleDocsUrl('')
          setGoogleDocsUrlInput('')
        }
      } catch (error) {
        if (error.status !== 404) {
          console.error('Error fetching Google Docs URL:', error)
        }
        setGoogleDocsExists(false)
        setGoogleDocsUrl('')
        setGoogleDocsUrlInput('')
      } finally {
        setGoogleDocsLoading(false)
      }
    }

    fetchGoogleDocsUrl()
    // eslint-disable-next-line
  }, [user, selectedCampaign, isAdmin])

  const handleOpenDialog = () => setOpenDialog(true)
  const handleCloseDialog = () => setOpenDialog(false)

  const validateFile = (file) => {
    if (!file) return { valid: false, error: 'Please select a file' }

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return { valid: false, error: 'Only SVG files are allowed' }
    }

    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: 'File size must be less than 5MB' }
    }

    return { valid: true }
  }

  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
      const validation = validateFile(file)
      if (validation.valid) {
        setSelectedFile(file)
      } else {
        alert(validation.error)
        event.target.value = ''
      }
    }
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
    const input = document.getElementById('file-input')
    if (input) input.value = ''
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    const campaign = getCurrentCampaign()
    if (!campaign) {
      alert('No campaign selected')
      return
    }

    setUploading(true)

    try {
      const formData = new FormData()
      formData.append('file', selectedFile)
      formData.append('campaignName', campaign)

      const response = await authenticatedFetch(apiEndpoints.campaignsSopUpload, {
        method: 'POST',
        body: formData
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Upload failed')
      }

      const data = await response.json()
      setSopUrl(data.sopUrl)
      setSopExists(true)
      setUploadDate(new Date().toISOString())
      setSelectedFile(null)
      setUploadConfirmDialog(false)
      const input = document.getElementById('file-input')
      if (input) input.value = ''
      showToast('SOP file uploaded successfully!', 'success')
    } catch (error) {
      showToast(error.message || 'Failed to upload file', 'error')
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (suppressAlert = false) => {
    const campaign = getCurrentCampaign()
    /* istanbul ignore next -- campaign always selected when delete button renders */
    if (!campaign) {
      alert('No campaign selected')
      return
    }

    setDeleting(true)

    try {
      const response = await authenticatedFetch(apiEndpoints.campaignSop(campaign), {
        method: 'DELETE'
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Delete failed')
      }

      setSopUrl('')
      setSopExists(false)
      setUploadDate(null)
      setDeleteConfirmDialog(false)
      setSelectedFile(null)
      const input = document.getElementById('file-input')
      if (input) input.value = ''
      if (!suppressAlert) {
        setTimeout(() => {
          showToast('SOP file deleted successfully!', 'success', <DeleteForever />, '#FF6B35')
        }, 100)
      }
    } catch (error) {
      showToast(error.message || 'Failed to delete file', 'error')
    } finally {
      setDeleting(false)
    }
  }

  const handleUploadConfirm = () => {
    if (!selectedFile) return
    setUploadConfirmDialog(true)
  }

  const handleCampaignChange = (event) => {
    setSelectedCampaign(event.target.value)
    setSelectedFile(null)
    const input = document.getElementById('file-input')
    if (input) input.value = ''
  }

  const handleGoogleDocsSave = async () => {
    /* istanbul ignore next -- guarded by disabled save button */
    if (!googleDocsUrlInput.trim()) {
      showToast('Please enter a Google Docs URL', 'error')
      return
    }

    const campaign = getCurrentCampaign()
    /* istanbul ignore next -- campaign always selected when save button renders */
    if (!campaign) {
      showToast('No campaign selected', 'error')
      return
    }

    if (!isValidGoogleDocsUrl(googleDocsUrlInput)) {
      showToast('Please enter a valid Google Docs URL', 'error')
      return
    }

    setGoogleDocsSaving(true)

    try {
      const response = await authenticatedFetch(apiEndpoints.campaignGoogleDocs(campaign), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ googleDocsUrl: googleDocsUrlInput.trim() })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to save Google Docs URL')
      }

      setGoogleDocsUrl(googleDocsUrlInput.trim())
      setGoogleDocsExists(true)
      showToast('Google Docs URL saved successfully!', 'success')
    } catch (error) {
      showToast(error.message || 'Failed to save Google Docs URL', 'error')
    } finally {
      setGoogleDocsSaving(false)
    }
  }

  const handleGoogleDocsDelete = async () => {
    const campaign = getCurrentCampaign()
    /* istanbul ignore next -- campaign always selected when delete button renders */
    if (!campaign) {
      showToast('No campaign selected', 'error')
      return
    }

    setGoogleDocsDeleting(true)

    try {
      const response = await authenticatedFetch(apiEndpoints.campaignGoogleDocs(campaign), {
        method: 'DELETE'
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to delete Google Docs URL')
      }

      setGoogleDocsUrl('')
      setGoogleDocsUrlInput('')
      setGoogleDocsExists(false)
      showToast('Google Docs URL removed successfully!', 'success', <Clear />, '#FF6B35')
    } catch (error) {
      showToast(error.message || 'Failed to delete Google Docs URL', 'error')
    } finally {
      setGoogleDocsDeleting(false)
    }
  }

  const handleGoogleDocsClear = () => {
    setGoogleDocsUrlInput('')
  }

  const handleGoogleDocsCancel = () => {
    setGoogleDocsUrlInput(googleDocsUrl)
  }

  return (
    <div className={styles.root}>
      <Box className={styles.pageHeader}>
        <Typography variant='h4' component='h1' className={styles.pageTitle}>
          SOP Settings
        </Typography>
        <Typography className={styles.pageSubtitle}>
          Manage standard operating procedure documents for campaigns
        </Typography>
      </Box>

      <div className={styles.cardsGrid}>
        {/* SOP File Management */}
        <div className={styles.cardSection}>
          <Box display='flex' alignItems='center' className={styles.cardTitle}>
            <Typography className={styles.sopUrlTitle}>SOP File Management</Typography>
            <Tooltip
              title='How to upload SOP files'
              arrow
              className={styles.urlTooltip}
              classes={{
                tooltip: styles.tooltipText
              }}
            >
              <IconButton size='small' className={styles.urlTooltip} onClick={handleOpenDialog}>
                <InfoIcon fontSize='medium' />
              </IconButton>
            </Tooltip>
          </Box>

          <Box className={styles.sopUrlWrapper}>
            {!loading && (
              <SOPFileCard
                isAdmin={isAdmin}
                campaigns={campaigns}
                selectedCampaign={selectedCampaign}
                handleCampaignChange={handleCampaignChange}
                loading={loading}
                sopExists={sopExists}
                uploadDate={uploadDate}
                getCurrentCampaign={getCurrentCampaign}
                selectedFile={selectedFile}
                uploading={uploading}
                deleting={deleting}
                handleFileSelect={handleFileSelect}
                handleRemoveFile={handleRemoveFile}
                handleUploadConfirm={handleUploadConfirm}
                setReuploadConfirmDialog={setReuploadConfirmDialog}
                setDeleteConfirmDialog={setDeleteConfirmDialog}
              />
            )}
          </Box>
        </div>

        {/* Google Docs SOP Management */}
        <div className={styles.cardSection}>
          <Box display='flex' alignItems='center' className={styles.cardTitle}>
            <Typography className={styles.sopUrlTitle}>Google Docs SOP</Typography>
            <InfoTooltip content='Manage Google Docs SOPs for text-based access' size='medium' />
          </Box>

          <Box className={styles.sopUrlWrapper}>
            <GoogleDocsCard
              isAdmin={isAdmin}
              campaigns={campaigns}
              selectedCampaign={selectedCampaign}
              handleCampaignChange={handleCampaignChange}
              googleDocsExists={googleDocsExists}
              googleDocsUrl={googleDocsUrl}
              googleDocsUrlInput={googleDocsUrlInput}
              setGoogleDocsUrlInput={setGoogleDocsUrlInput}
              googleDocsSaving={googleDocsSaving}
              googleDocsDeleting={googleDocsDeleting}
              handleGoogleDocsSave={handleGoogleDocsSave}
              handleGoogleDocsDelete={handleGoogleDocsDelete}
              handleGoogleDocsClear={handleGoogleDocsClear}
              handleGoogleDocsCancel={handleGoogleDocsCancel}
              getCurrentCampaign={getCurrentCampaign}
            />
          </Box>
        </div>
      </div>

      {/* Info Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth='md'
        PaperProps={{
          style: { borderRadius: 16, padding: 16 }
        }}
        className={styles.dialogWrapper}
      >
        <DialogTitle className={styles.dialogTitle}>How to Upload SOP File</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <Box p={2} className={styles.dialogBox}>
            <Typography paragraph className={styles.dialogText}>
              1. Create or export your SOP as an image in{' '}
              <span className={styles.dialogKeyword}>.svg</span> format.
              <br />
              <br />
              2. Click the <strong>Choose File</strong> button below to select your SVG file.
              <br />
              <br />
              3. Ensure your file is:
              <br />
              &bull; In <span className={styles.dialogKeyword}>.svg</span> format
              <br />
              &bull; Less than <span className={styles.dialogKeyword}>5MB</span> in size
              <br />
              <br />
              4. Click <strong>Upload</strong> to save your SOP file to the system.
              <br />
              <br />
              5. The file will be securely stored and automatically displayed in your application.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={handleCloseDialog} className={styles.closeButton} disableRipple>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Confirmation Dialog */}
      <Dialog
        open={uploadConfirmDialog}
        onClose={() => setUploadConfirmDialog(false)}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: 16,
            backgroundColor: 'var(--color-bg-white)',
            color: 'var(--color-text-primary)'
          }
        }}
      >
        <DialogTitle>
          <Typography variant='h6' style={{ color: 'var(--color-text-primary)' }}>
            {sopExists ? 'Replace SOP File' : 'Upload SOP File'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box display='flex' alignItems='center' style={{ marginBottom: '1rem' }}>
            <Description style={{ color: 'var(--color-text-primary)', marginRight: '1rem' }} />
            <Box>
              <Typography variant='body1' style={{ fontWeight: '500' }}>
                {selectedFile?.name}
              </Typography>
              <Typography variant='caption' color='textSecondary'>
                {selectedFile ? (selectedFile.size / 1024).toFixed(1) : 0} KB
              </Typography>
            </Box>
          </Box>
          <Typography variant='body2' color='textSecondary'>
            {sopExists
              ? `This will replace the existing SOP file for "${getCurrentCampaign()}". The old file will be permanently deleted.`
              : `This will upload a new SOP file for campaign "${getCurrentCampaign()}".`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUploadConfirmDialog(false)}
            disabled={uploading}
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={uploading}
            variant='contained'
            style={{
              backgroundColor: 'var(--color-deep-blue-bg)',
              color: '#fff',
              borderRadius: 10
            }}
            startIcon={<CloudUpload />}
          >
            {uploading ? 'Uploading...' : sopExists ? 'Replace' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog}
        onClose={() => setDeleteConfirmDialog(false)}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: 16,
            backgroundColor: 'var(--color-bg-white)',
            color: 'var(--color-text-primary)'
          }
        }}
      >
        <DialogTitle>
          <Typography variant='h6' style={{ color: 'var(--color-error)' }}>
            Delete SOP File
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant='body1' style={{ marginBottom: '1rem' }}>
            Are you sure you want to delete the SOP file for &quot;{getCurrentCampaign()}&quot;?
          </Typography>
          <Typography variant='body2' color='textSecondary'>
            This action cannot be undone. The file will be permanently removed from the system.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmDialog(false)}
            disabled={deleting}
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDelete(false)}
            disabled={deleting}
            variant='contained'
            style={{ backgroundColor: 'var(--color-error)', color: '#fff', borderRadius: 10 }}
            startIcon={<Delete />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reupload Confirmation Dialog */}
      <Dialog
        open={reuploadConfirmDialog}
        onClose={() => setReuploadConfirmDialog(false)}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: 16,
            backgroundColor: 'var(--color-bg-white)',
            color: 'var(--color-text-primary)'
          }
        }}
      >
        <DialogTitle>
          <Typography variant='h6' style={{ color: 'var(--color-text-primary)' }}>
            Reupload SOP File
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant='body1' style={{ marginBottom: '1rem' }}>
            This will delete the current SOP file for &quot;{getCurrentCampaign()}&quot; and allow
            you to upload a new one.
          </Typography>
          <Typography variant='body2' color='textSecondary'>
            Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setReuploadConfirmDialog(false)}
            disabled={deleting}
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              setReuploadConfirmDialog(false)
              await handleDelete(true)
              setTimeout(() => {
                const input = document.getElementById('file-input')
                if (input) {
                  input.value = ''
                  input.click()
                }
              }, 300)
            }}
            disabled={deleting}
            variant='contained'
            style={{
              backgroundColor: 'var(--color-deep-blue-bg)',
              color: '#fff',
              borderRadius: 10
            }}
            startIcon={<CloudUpload />}
          >
            {deleting ? 'Deleting...' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        icon={toast.icon}
        customColor={toast.customColor}
        onClose={closeToast}
      />
    </div>
  )
}

SettingsSOPPage.propTypes = {
  user: PropTypes.object
}

export default SettingsSOPPage
